<script setup lang="ts">
import { computed } from 'vue';
import { t } from './i18n';
import { generateCodeSvg, previewSvg, type CodeDesignItem } from './codeSvg';

type DesignerCommands = {
  addItem?: (item: CodeDesignItem) => void;
  selectItem?: (id: string) => void;
};

const props = defineProps<{
  context?: Record<string, unknown>;
}>();

const previews = computed(() => ({
  qrcode: previewSvg(generateCodeSvg(defaultCodeItem('qrcode')).svg),
  barcode: previewSvg(generateCodeSvg(defaultCodeItem('barcode')).svg),
}));

function nextId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `code-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function defaultCodeItem(type: 'qrcode' | 'barcode'): CodeDesignItem {
  const base = {
    id: nextId(),
    type,
    top: 50,
    left: 50,
    zIndex: 0,
    angle: 0,
    opacity: 1,
    fgColor: '#000000',
    bgColor: '',
  };
  if (type === 'barcode') {
    const item = {
      ...base,
      width: 220,
      height: 90,
      codeData: '5901234123457',
      barcodeFormat: 'EAN13',
      showText: true,
    };
    return { ...item, svg: generateCodeSvg(item).svg };
  }
  const item = {
    ...base,
    width: 140,
    height: 140,
    codeData: 'https://example.com',
    ecLevel: 'M',
  };
  return { ...item, svg: generateCodeSvg(item).svg };
}

function commands(): DesignerCommands | null {
  return (window as unknown as { Veyop?: { designer?: { commands?: DesignerCommands } } }).Veyop?.designer?.commands ?? null;
}

function addCode(type: 'qrcode' | 'barcode', event?: MouseEvent) {
  const item = defaultCodeItem(type);
  const cmd = commands();
  cmd?.addItem?.(item);
  cmd?.selectItem?.(String(item.id));
  // Editing continues on the canvas: a tile left focused lights up its focus ring as soon as the
  // shopper uses the keyboard there (Delete, arrows).
  (event?.currentTarget as HTMLElement | null)?.blur();
}
</script>

<template>
  <section class="vptw:flex vptw:flex-col vptw:gap-2 vptw:p-2" :aria-label="t('Codes')">
    <button
      type="button"
      class="vptw:w-full! vptw:cursor-pointer vptw:rounded-xl! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-2! vptw:text-left! vptw:transition vptw:hover:border-(--vs-accent)! vptw:hover:bg-(--vs-accent-soft)!"
      @click="addCode('qrcode', $event)"
    >
      <span class="vptw:flex vptw:items-center vptw:gap-2">
        <span class="vptw:flex vptw:size-16 vptw:shrink-0 vptw:items-center vptw:justify-center vs-code-preview vptw:rounded-lg vptw:p-2" v-html="previews.qrcode" />
        <span class="vptw:min-w-0">
          <span class="vptw:block vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink)">{{ t('QR code') }}</span>
          <span class="vptw:mt-1 vptw:block vptw:text-xs vptw:leading-5 vptw:text-(--vs-ink-2)">{{ t('URL or text') }}</span>
        </span>
      </span>
    </button>

    <button
      type="button"
      class="vptw:w-full! vptw:cursor-pointer vptw:rounded-xl! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-2! vptw:text-left! vptw:transition vptw:hover:border-(--vs-accent)! vptw:hover:bg-(--vs-accent-soft)!"
      @click="addCode('barcode', $event)"
    >
      <span class="vptw:flex vptw:items-center vptw:gap-2">
        <span class="vptw:flex vptw:h-14 vptw:w-20 vptw:shrink-0 vptw:items-center vptw:justify-center vs-code-preview vptw:rounded-lg vptw:p-2" v-html="previews.barcode" />
        <span class="vptw:min-w-0">
          <span class="vptw:block vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink)">{{ t('Barcode') }}</span>
          <span class="vptw:mt-1 vptw:block vptw:text-xs vptw:leading-5 vptw:text-(--vs-ink-2)">CODE128, EAN13, EAN8</span>
        </span>
      </span>
    </button>
  </section>
</template>
